/**
 * Interface de Saisie Rapide pour Relevés V2
 * Interface ultra-optimisée pour saisie manuelle rapide avec navigation clavier
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetLignesOCRManuscritV2Query,
  useValiderLigneOCRManuscritV2Mutation,
  useValiderToutReleveManuscritV2Mutation,
} from '../../services/ocrApiV2';
import { useGetRelevesCorrectionQuery } from '../../services/ocrApi';
import {
  Keyboard,
  Zap,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Save,
  Trophy,
  Clock,
  Target,
} from 'lucide-react';

const SaisieRapideRelevesV2: React.FC = () => {
  const { releveId } = useParams<{ releveId: string }>();
  const navigate = useNavigate();

  // States
  const [ligneIndex, setLigneIndex] = useState(0);
  const [noteInput, setNoteInput] = useState('');
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [tempsEcoule, setTempsEcoule] = useState(0);
  const [notesModifiees, setNotesModifiees] = useState(0);
  const [erreurValidation, setErreurValidation] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // Queries
  const { data: releves } = useGetRelevesCorrectionQuery();
  const { data: lignes, isLoading, refetch } = useGetLignesOCRManuscritV2Query(
    { releveId: parseInt(releveId!), aVerifier: false },
    { skip: !releveId }
  );

  // Mutations
  const [validerLigne, { isLoading: validerLoading }] = useValiderLigneOCRManuscritV2Mutation();
  const [validerTout, { isLoading: validerToutLoading }] = useValiderToutReleveManuscritV2Mutation();

  const releve = releves?.find(r => r.id === parseInt(releveId!));
  const ligneActuelle = lignes?.[ligneIndex];

  // Timer
  useEffect(() => {
    const interval = setInterval(() => {
      setTempsEcoule(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  // Focus automatique
  useEffect(() => {
    inputRef.current?.focus();
  }, [ligneIndex]);

  // Format temps (MM:SS)
  const formatTemps = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Validation de la note
  const validerNote = (input: string): { valide: boolean; note?: number; erreur?: string } => {
    if (!input.trim()) {
      return { valide: false, erreur: 'Note requise' };
    }

    // Accepter formats: 15.5, 1550, 15,5, 08, 8, 20
    let noteStr = input.trim().replace(',', '.');

    // Si 4 chiffres sans point: 1550 → 15.50
    if (/^\d{4}$/.test(noteStr)) {
      noteStr = noteStr.slice(0, 2) + '.' + noteStr.slice(2);
    }
    // Si 3 chiffres: 850 → 8.50
    else if (/^\d{3}$/.test(noteStr)) {
      noteStr = noteStr[0] + '.' + noteStr.slice(1);
    }
    // Si 2 chiffres sans point: 08 → 8, 20 → 20
    else if (/^\d{2}$/.test(noteStr)) {
      const num = parseInt(noteStr);
      noteStr = num.toString();
    }

    const note = parseFloat(noteStr);

    if (isNaN(note)) {
      return { valide: false, erreur: 'Format invalide' };
    }

    if (note < 0 || note > 20) {
      return { valide: false, erreur: 'Note entre 0 et 20' };
    }

    return { valide: true, note };
  };

  // Sauvegarder et passer à la suivante
  const handleSauvegarderEtContinuer = async () => {
    if (!ligneActuelle) return;

    const validation = validerNote(noteInput);
    if (!validation.valide) {
      setErreurValidation(validation.erreur || 'Erreur');
      return;
    }

    try {
      await validerLigne({
        releveId: parseInt(releveId!),
        ligneId: ligneActuelle.id,
        note_finale: validation.note!,
        commentaire: 'Saisie manuelle rapide',
      }).unwrap();

      setNotesModifiees(prev => prev + 1);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 300);

      // Passer à la ligne suivante
      if (ligneIndex < (lignes?.length || 0) - 1) {
        setLigneIndex(prev => prev + 1);
        setNoteInput('');
        setErreurValidation('');
      } else {
        // Dernière ligne - terminer
        handleTerminer();
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setErreurValidation('Erreur sauvegarde');
    }
  };

  // Navigation clavier
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSauvegarderEtContinuer();
    } else if (e.key === 'ArrowUp' && ligneIndex > 0) {
      e.preventDefault();
      setLigneIndex(prev => prev - 1);
      setNoteInput('');
      setErreurValidation('');
    } else if (e.key === 'ArrowDown' && ligneIndex < (lignes?.length || 0) - 1) {
      e.preventDefault();
      setLigneIndex(prev => prev + 1);
      setNoteInput('');
      setErreurValidation('');
    }
  };

  // Terminer
  const handleTerminer = async () => {
    if (!window.confirm('Terminer la saisie et valider toutes les notes ?')) {
      return;
    }

    try {
      await validerTout(parseInt(releveId!)).unwrap();
      alert(`Saisie terminée en ${formatTemps(tempsEcoule)}! ${notesModifiees} notes enregistrées.`);
      navigate('/ocr/generation-correction-v2');
    } catch (error: any) {
      alert(error?.data?.message || 'Erreur lors de la validation');
    }
  };

  const progression = lignes?.length ? ((ligneIndex + 1) / lignes.length) * 100 : 0;
  const vitesseMoyenne = notesModifiees > 0 ? tempsEcoule / notesModifiees : 0;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!lignes || lignes.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
          <AlertCircle className="mx-auto mb-4 text-yellow-600" size={48} />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Aucune ligne à saisir</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Ce relevé ne contient aucune ligne.</p>
          <button
            onClick={() => navigate('/ocr/generation-correction-v2')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-6">
      {/* Header avec stats */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/ocr/generation-correction-v2')}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-2"
          >
            <ChevronLeft size={20} />
            Retour
          </button>

          <div className="flex items-center gap-4">
            {/* Temps écoulé */}
            <div className="bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow flex items-center gap-2">
              <Clock size={20} className="text-blue-600" />
              <span className="font-mono text-lg font-bold dark:text-gray-100">{formatTemps(tempsEcoule)}</span>
            </div>

            {/* Vitesse moyenne */}
            <div className="bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow flex items-center gap-2">
              <Zap size={20} className="text-yellow-600" />
              <span className="text-sm font-medium dark:text-gray-300">
                {vitesseMoyenne > 0 ? `${vitesseMoyenne.toFixed(1)}s/note` : '--'}
              </span>
            </div>

            {/* Notes saisies */}
            <div className="bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow flex items-center gap-2">
              <Target size={20} className="text-green-600" />
              <span className="text-sm font-medium dark:text-gray-300">{notesModifiees} saisies</span>
            </div>
          </div>
        </div>

        {/* Barre de progression */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Saisie Rapide - {releve?.code_releve}
            </h1>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Ligne {ligneIndex + 1} / {lignes.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-green-500 h-full transition-all duration-300 ease-out"
              style={{ width: `${progression}%` }}
            />
          </div>
          <div className="text-right text-xs text-gray-500 dark:text-gray-400 mt-1">
            {progression.toFixed(0)}% complété
          </div>
        </div>
      </div>

      {/* Interface principale */}
      <div className="max-w-6xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
          <div className="grid grid-cols-2 gap-0">
            {/* Panneau gauche - Image du scan */}
            <div className="bg-gray-900 p-6 flex flex-col">
              <div className="flex-1 flex items-center justify-center">
                {ligneActuelle?.image_base64 ? (
                  <img
                    src={`data:image/png;base64,${ligneActuelle.image_base64}`}
                    alt="Ligne scan"
                    className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                  />
                ) : (
                  <div className="text-gray-400 text-center">
                    <AlertCircle size={48} className="mx-auto mb-2" />
                    <p>Aucune image disponible</p>
                  </div>
                )}
              </div>
            </div>

            {/* Panneau droit - Saisie */}
            <div className="p-8 flex flex-col justify-center bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-gray-900">
              {/* Info étudiant */}
              <div className="mb-8 text-center">
                <div className="inline-block px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-lg mb-4">
                  <span className="font-mono font-bold text-2xl">
                    {ligneActuelle?.code_copie_detecte || 'N/A'}
                  </span>
                </div>
                {ligneActuelle?.copie_examen?.etudiant && (
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {ligneActuelle.copie_examen.etudiant.nom}{' '}
                    {ligneActuelle.copie_examen.etudiant.prenoms}
                  </h2>
                )}
              </div>

              {/* Champ de saisie */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Saisir la note (ex: 1550 pour 15.50)
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  value={noteInput}
                  onChange={(e) => {
                    setNoteInput(e.target.value);
                    setErreurValidation('');
                  }}
                  onKeyDown={handleKeyDown}
                  className={`w-full px-6 py-4 text-4xl font-bold text-center border-4 rounded-xl focus:outline-none focus:ring-4 transition-all dark:bg-gray-700 dark:text-gray-100 ${
                    erreurValidation
                      ? 'border-red-500 focus:ring-red-200 dark:focus:ring-red-900'
                      : 'border-blue-500 focus:ring-blue-200 dark:focus:ring-blue-900'
                  }`}
                  placeholder="____"
                  autoComplete="off"
                />
                {erreurValidation && (
                  <p className="mt-2 text-red-600 text-sm font-medium flex items-center gap-1">
                    <AlertCircle size={16} />
                    {erreurValidation}
                  </p>
                )}
              </div>

              {/* Boutons d'action */}
              <div className="space-y-3">
                <button
                  onClick={handleSauvegarderEtContinuer}
                  disabled={validerLoading}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {validerLoading ? (
                    'Sauvegarde...'
                  ) : (
                    <>
                      <CheckCircle2 size={24} />
                      Sauvegarder et Continuer (Enter)
                    </>
                  )}
                </button>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      if (ligneIndex > 0) {
                        setLigneIndex(prev => prev - 1);
                        setNoteInput('');
                        setErreurValidation('');
                      }
                    }}
                    disabled={ligneIndex === 0}
                    className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={20} />
                    Précédent
                  </button>

                  <button
                    onClick={() => {
                      if (ligneIndex < lignes.length - 1) {
                        setLigneIndex(prev => prev + 1);
                        setNoteInput('');
                        setErreurValidation('');
                      }
                    }}
                    disabled={ligneIndex === lignes.length - 1}
                    className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>

              {/* Raccourcis clavier */}
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Keyboard size={16} />
                  <span className="font-medium">Raccourcis:</span>
                  <kbd className="px-2 py-1 bg-white dark:bg-gray-700 rounded shadow text-xs font-mono dark:text-gray-300">Enter</kbd>
                  <span className="text-xs">Sauvegarder</span>
                  <kbd className="px-2 py-1 bg-white dark:bg-gray-700 rounded shadow text-xs font-mono dark:text-gray-300">↑↓</kbd>
                  <span className="text-xs">Naviguer</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bouton Terminer */}
        {ligneIndex === lignes.length - 1 && (
          <div className="mt-6 text-center">
            <button
              onClick={handleTerminer}
              disabled={validerToutLoading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-8 rounded-xl flex items-center justify-center gap-3 transition-all transform hover:scale-105 shadow-lg mx-auto"
            >
              <Trophy size={24} />
              Terminer et Valider Tout
            </button>
          </div>
        )}
      </div>

      {/* Feedback visuel succès */}
      {showSuccess && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-bounce">
          <CheckCircle2 size={20} />
          Sauvegardé!
        </div>
      )}
    </div>
  );
};

export default SaisieRapideRelevesV2;
